import { defineStore } from 'pinia';
import api from '@/services/api';

/**
 * Users store - manages user authentication and user-related data
 */
export const useUsersStore = defineStore('users', {
  // State
  state: () => ({
    currentUser: JSON.parse(localStorage.getItem('currentUser')) || null,
    users: [],
    isAuthenticated: !!localStorage.getItem('currentUser'),
    token: localStorage.getItem('token') || null,
    isLoading: false,
    error: null,
    permissions: [],
    roles: [],
    accounts: [],
  }),

  // Getters
  getters: {
    // Check if user is logged in
    isLoggedIn: (state) => !!state.currentUser,

    // Get current user details
    getCurrentUser: (state) => state.currentUser,

    // Get user by ID
    getUserById: (state) => (id) => {
      return state.users.find((user) => user.id === id);
    },

    // Get account by ID
    getAccountById: (state) => (id) => {
      return state.accounts.find((account) => account.id === id);
    },

    // Check if user has specific role
    hasRole: (state) => (role) => {
      if (!state.currentUser || !state.currentUser.roles) return false;
      return state.currentUser.roles.includes(role);
    },

    // Check if user has specific permission
    hasPermission: (state) => (permission) => {
      if (!state.currentUser || !state.permissions) return false;
      return state.permissions.includes(permission);
    },

    // Get user's avatar URL or default if not set
    userAvatar: (state) => {
      if (!state.currentUser) return '/assets/images/avatar/default.png';
      return state.currentUser.avatar || '/assets/images/avatar/default.png';
    },
  },

  // Actions
  actions: {
    // Login user
    async login(credentials) {
      this.isLoading = true;
      this.error = null;

      try {
        // Here would be the API call
        // Example: const response = await api.post('/auth/login', credentials)

        // Placeholder implementation
        const { email, password } = credentials;

        // Basic validation (for demo purposes)
        if (email === 'admin@example.com' && password === 'password') {
          // Mock successful login
          const userData = {
            id: 1,
            email: 'admin@example.com',
            name: 'Admin User',
            roles: ['admin'],
            avatar: null,
          };

          const token = 'mock-jwt-token';

          // Set user data in state and localStorage
          this.setUserData(userData, token);

          // Fetch user permissions
          await this.fetchUserPermissions();

          return { success: true, user: userData };
        } else {
          throw new Error('Invalid email or password');
        }
      } catch (err) {
        this.error = err.message || 'Login failed';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Set user data after successful authentication
    setUserData(userData, token) {
      this.currentUser = userData;
      this.token = token;
      this.isAuthenticated = true;

      // Store in localStorage
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('token', token);
    },

    // Logout user
    logout() {
      // Clear state
      this.currentUser = null;
      this.token = null;
      this.isAuthenticated = false;
      this.permissions = [];

      // Clear localStorage
      localStorage.removeItem('currentUser');
      localStorage.removeItem('token');

      // Here would be an API call to invalidate token (optional)
      // Example: await api.post('/auth/logout')
    },

    // Register new user
    async register(userData) {
      this.isLoading = true;
      this.error = null;

      try {
        // Here would be the API call
        // Example: const response = await api.post('/auth/register', userData)

        // Placeholder implementation (for demo purposes)
        const newUser = {
          id: this.users.length + 1,
          ...userData,
          roles: ['user'],
          avatar: null,
        };

        // Add to users list
        this.users.push(newUser);

        return { success: true, user: newUser };
      } catch (err) {
        this.error = err.message || 'Registration failed';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch all users (admin only)
    async fetchUsers() {
      this.isLoading = true;

      try {
        // Here would be the API call
        // Example: const response = await api.get('/users')

        // Placeholder implementation
        setTimeout(() => {
          this.users = [
            {
              id: 1,
              name: 'Admin User',
              email: 'admin@example.com',
              roles: ['admin'],
              avatar: null,
            },
            {
              id: 2,
              name: 'Regular User',
              email: 'user@example.com',
              roles: ['user'],
              avatar: null,
            },
          ];
          this.error = null;
          this.isLoading = false;
        }, 500);
      } catch (err) {
        this.error = err.message || 'Failed to fetch users';
        this.isLoading = false;
      }
    },

    // Fetch accounts (implement API endpoint)
    async fetchAccounts(params = {}) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.accounts.getAll(params);
        this.accounts = response.data.items || response.data;
        return this.accounts;
      } catch (err) {
        this.error = err.message || 'Falha ao buscar contas de usuário';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch roles
    async fetchRoles() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.accounts.getRoles();
        this.roles = response.data;
        return this.roles;
      } catch (err) {
        this.error = err.message || 'Falha ao buscar papéis de usuário';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch current user profile
    async fetchMyProfile() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.accounts.getMe();

        // Extrair os dados do usuário do campo data da resposta
        const userData = response.data.data;
        this.currentUser = userData;

        // Update localStorage
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser));

        return this.currentUser;
      } catch (err) {
        this.error = err.message || 'Falha ao buscar perfil do usuário';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch account by ID
    async fetchAccountById(id) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.accounts.getById(id);
        // Optionally update the accounts array with this account
        const accountIndex = this.accounts.findIndex(
          (account) => account.id === id
        );
        if (accountIndex >= 0) {
          this.accounts[accountIndex] = response.data;
        } else {
          this.accounts.push(response.data);
        }
        return response.data;
      } catch (err) {
        this.error = err.message || 'Falha ao buscar detalhes da conta';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Update account
    async updateAccount(id, accountData) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.accounts.update(id, accountData);

        // Update account in the accounts array
        const accountIndex = this.accounts.findIndex(
          (account) => account.id === id
        );
        if (accountIndex >= 0) {
          this.accounts[accountIndex] = response.data;
        }

        // If updating current user, also update currentUser state
        if (this.currentUser && this.currentUser.id === id) {
          this.currentUser = { ...this.currentUser, ...response.data };
          localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
        }

        return response.data;
      } catch (err) {
        this.error = err.message || 'Falha ao atualizar conta';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Delete account (soft delete)
    async deleteAccount(id) {
      this.isLoading = true;
      this.error = null;

      try {
        await api.accounts.delete(id);

        // Remove account from accounts array
        this.accounts = this.accounts.filter((account) => account.id !== id);

        return { success: true };
      } catch (err) {
        this.error = err.message || 'Falha ao desativar conta';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Delete account permanently
    async deleteAccountPermanently(id) {
      this.isLoading = true;
      this.error = null;

      try {
        await api.accounts.deletePermanent(id);

        // Remove account from accounts array
        this.accounts = this.accounts.filter((account) => account.id !== id);

        return { success: true };
      } catch (err) {
        this.error = err.message || 'Falha ao remover conta permanentemente';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Update user profile
    async updateUserProfile(updates) {
      if (!this.currentUser) {
        throw new Error('No authenticated user');
      }

      this.isLoading = true;

      try {
        // Here would be the API call
        // Example: const response = await api.put(`/users/${this.currentUser.id}`, updates)

        // Placeholder implementation
        const updatedUser = {
          ...this.currentUser,
          ...updates,
        };

        // Update current user
        this.currentUser = updatedUser;
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));

        // If the updated user is also in the users list, update there too
        const index = this.users.findIndex((u) => u.id === updatedUser.id);
        if (index > -1) {
          this.users[index] = updatedUser;
        }

        return updatedUser;
      } catch (err) {
        this.error = err.message || 'Failed to update profile';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch user permissions
    async fetchUserPermissions() {
      if (!this.currentUser) return;

      this.isLoading = true;

      try {
        // Here would be the API call
        // Example: const response = await api.get(`/users/${this.currentUser.id}/permissions`)

        // Placeholder implementation based on user role
        if (this.currentUser.roles.includes('admin')) {
          this.permissions = [
            'create:user',
            'read:user',
            'update:user',
            'delete:user',
            'create:supplier',
            'read:supplier',
            'update:supplier',
            'delete:supplier',
            'create:category',
            'read:category',
            'update:category',
            'delete:category',
            'update:settings',
          ];
        } else {
          this.permissions = ['read:user', 'read:supplier', 'read:category'];
        }
      } catch (err) {
        this.error = err.message || 'Failed to fetch permissions';
      } finally {
        this.isLoading = false;
      }
    },

    // Check authentication status and refresh if needed
    async checkAuth() {
      if (!this.token) return false;

      // Here you could implement token verification or refresh logic
      // Example: Check token expiration and refresh if needed

      // For demo purposes, we'll just check if token exists
      return !!this.token;
    },
  },
});
